<template>
  <div class="work-header-wrapper header-shadow">
    <WorkMobileHeaderBar
      v-if="$vuetify.breakpoint.smAndDown"
      :go-to-navigation="goToNavigation"
      :go-to-dashboard="goToDashboard"
      :go-to-open-windows="goToOpenWindows"
      :toggle-theme="toggleTheme"
      :go-to-user-profile="goToUserProfile"
      :show-deputy-modal="showDeputyModal"
      :show-dashboard="showDashboard"
      :logout-as-deputy="logoutAsDeputy"
      :logout="invokeLogout"
      :deputy-exists="deputyExists"
      :languages="languages"
      :show-help-window="showHelpWindow"
    />
    <WorkDesktopHeaderBar
      v-else
      :go-to-navigation="goToNavigation"
      :go-to-open-windows="goToOpenWindows"
      :go-to-dashboard="goToDashboard"
      :toggle-theme="toggleTheme"
      :go-to-user-profile="goToUserProfile"
      :navigate-to-create-message-form="navigateToCreateMessageForm"
      :show-deputy-modal="showDeputyModal"
      :logout-as-deputy="logoutAsDeputy"
      :logout="invokeLogout"
      :deputy-exists="deputyExists"
      :languages="languages"
      :show-help-window="showHelpWindow"
      :show-dashboard="showDashboard"
      :use-custom-logo="useCustomLogo"
      :custom-logo-test-path="customLogoTestPath"
      :custom-logo-prod-path="customLogoProdPath"
      :customer-instance="customerInstance"
      :disable-message-add-button="disableMessageAddButton"
    />

    <HelpWindow ref="helpWindow" />
    <DeputyDialog ref="deputyDialog" />
    <ForceTemporaryPasswordChangeDialog
      ref="forceTemporaryPasswordChangeDialog" />
  </div>
</template>

<script>
import actingUserService from '@/services/acting-user-service';
import themeService from '@/services/theme-service';
import { mapGetters, mapState, mapActions } from 'vuex';
import WorkDesktopHeaderBar from './WorkDesktopHeaderBar';
import WorkAuthentication from '@/services/authentication_services/WorkAuthentication';
import ForceTemporaryPasswordChangeDialog from '@/modules/login/components/ForceTemporaryPasswordChangeDialog.vue'
import WorkMobileHeaderBar from './WorkMobileHeaderBar';
import HelpWindow from '@/modules/help/components/HelpWindow';
import DeputyDialog from '@/components/global_components/dialogs/DeputyDialog';
import formUtils from '@/utils/form-utils';
import registryMixin from "@/mixins/registry-mixin";

const entityFormMapping = [
  { entity: 'addressPerson', form: 'ContactForm' },
  { entity: 'project', form: 'ProjectForm' },
  { entity: 'device', form: 'DevicesForm' },
  { entity: 'product', form: 'ProductForm' },
  { entity: 'activity', form: 'ActivityForm' }
];

export default {
  name: 'WorkHeaderWrapper',
  components: {
    WorkDesktopHeaderBar,
    WorkMobileHeaderBar,
    HelpWindow,
    DeputyDialog,
    ForceTemporaryPasswordChangeDialog
  },
  props: {
    enableThemeSwitch: { type: Boolean, default: true },
    useCustomLogo: { type: Boolean, default: false },
    customLogoTestPath: { type: String, default: undefined },
    customLogoProdPath: { type: String, default: undefined },
    customerInstance: { type: String, default: undefined },
    disableMessageAddButton: { type: Boolean, required: false },
    showDashboard: { type: Boolean, default: true },
  },
  async mounted() {
    // this is necessary because the loadRegistries call in the router cannot apply here because the user is never routed to /session/storage
    // this needs to be loaded independently of where the user is routed to since its the header bar
    if (!this.getRegistry('configurations/session/storage'))
      await this.loadRegistries('configurations/session/storage');
  },
  mixins: [
    registryMixin(
      'removeActiveWindowsOnLogout',
      'Remove.Active.Windows.On.Logout',
      'configurations/session/storage'
    ),
  ],
  data() {
    return {
      languages: [
        { flag: 'us', language: 'en', title: this.$t('Common.english') },
        { flag: 'de', language: 'de', title: this.$t('Common.german') },
        { flag: 'ru', language: 'ru', title: this.$t('Common.russian') },
      ],
      name: '',
      activeWindowsDialogMessage: this.$t(
        'Components.sentences.openWindowsDiscardChanges'
      ),
      activeWindowsDialogAcceptText: '',
    };
  },
  computed: {
    ...mapState(['activeModule']),
    ...mapState('actingUser', ['actingUserDetails']),
    ...mapGetters('badgeCounter', ['getActiveWindowsCount']),
    ...mapGetters('actingUser', ['getUserToActFor']),
    ...mapGetters('registries', ['getRegistry']),
    filterId() {
      return this.$route.params.id;
    },
    filterEntity() {
      const efm = entityFormMapping.find(e => e.form === this.$route.name);
      if (efm) {
        return efm.entity;
      }
      return undefined;
    },
    thereAreActiveWindows() {
      return this.getActiveWindowsCount > 0;
    },
    deputyExists() {
      return this.$store.state.actingUser.userToActFor;
    },
  },
  created() {
    actingUserService.refreshActingUserInfo();
  },
  methods: {
    ...mapActions('sidebarDrawer', ['setSelectedGroup']),
    ...mapActions('registries', ['loadRegistries']),
    /*******************************************
     * HELP
     *******************************************/
    showHelpWindow() {
      this.$refs.helpWindow.open(this.$route.meta.moduleId);
    },
    navigateToCreateMessageForm() {
      this.$router.push({
        name: 'MessageForm',
        params: {
          id: formUtils.createNewIdentifier(),
          initialDescription: `<br/><br/><a href="${window.location.origin + this.$route.path}">${window.location.origin + this.$route.path}</a>`
        }
      });
    },
    /*******************************************
     * GO TO
     *******************************************/
    goToNavigation() {
      this.$router.push('/navigation').catch(() => { });
      this.setSelectedGroup(null);
    },
    goToOpenWindows() {
      this.$router.push('/openWindows').catch(() => { });
    },
    goToDashboard() {
      this.$router.push('/dashboard').catch(() => { });
    },
    goToUserProfile() {
      this.$router.push('/user-profile').catch(() => { });
    },
    /*******************************************
     * THEME
     *******************************************/
    toggleTheme() {
      if (confirm(this.$t('Components.sentences.darkModePrompt'))) {
        this.$vuetify.theme.dark = !this.$vuetify.theme.dark;
      }
    },
    /*******************************************
     * ACTING USER
     *******************************************/
    showDeputyModal() {
      this.$refs.deputyDialog.open().then(async (item) => {
        if (this.thereAreActiveWindows && this.removeActiveWindowsOnLogout) {
          this.setActiveWindowsDialogToBeforeSwitchToDeputy();
          await this.openActiveWindowsDialog();
          actingUserService.setActingUser(item.userId, item.deputyTypeId);
        } else {
          actingUserService.setActingUser(item.userId, item.deputyTypeId);
        }
      });
    },
    setActingUser(id) {
      actingUserService.setActingUser(id);
    },
    removeActingUser() {
      actingUserService.removeActingUser();
    },
    async logoutAsDeputy() {
      if (this.thereAreActiveWindows && this.removeActiveWindowsOnLogout) {
        this.setActiveWindowsDialogToBeforeLogoutAsDeputy();
        await this.openActiveWindowsDialog();
        actingUserService.removeActingUser();
      } else {
        actingUserService.removeActingUser();
      }
    },
    setActiveWindowsDialogToBeforeSwitchToDeputy() {
      this.activeWindowsDialogAcceptText = this.$t('Deputies.login');
    },
    setActiveWindowsDialogToBeforeLogoutAsDeputy() {
      this.activeWindowsDialogAcceptText = this.$t('Deputies.logout');
    },
    /*******************************************
     * LOGOUT
     *******************************************/
    async invokeLogout() {
      if (this.thereAreActiveWindows && this.removeActiveWindowsOnLogout) {
        this.setActiveWindowsDialogToBeforeLogout();
        await this.openActiveWindowsDialog();
        this.logout();
      } else {
        this.logout();
      }
    },
    logout() {
      WorkAuthentication.logout();
    },
    setActiveWindowsDialogToBeforeLogout() {
      this.activeWindowsDialogAcceptText = this.$t('Common.logout');
    },
    async openActiveWindowsDialog() {
      await this.$root.$warningDialog.open({
        message: this.activeWindowsDialogMessage,
        resolveLabel: this.activeWindowsDialogAcceptText,
      });
    },
  },
  watch: {
    actingUserDetails(val) {
      if (val?.hasTemporaryPassword && !this.getUserToActFor)
        this.$refs.forceTemporaryPasswordChangeDialog.open();

      if (val && this.enableThemeSwitch) {
        themeService.setTheme(val, this.$vuetify.theme);
      }
    }
  }
};
</script>

<style lang="scss">
.work-header-wrapper {
  height: $nav-bar-height;
  z-index: 9999;
}
</style>
